'use client';

import React from 'react';
import {
  Table as MuiTable,
  TableContainer,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHeader,
  TableRow as MuiTableRow,
  Typography,
} from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export const TableCell = (props: React.PropsWithChildren) => {
  const { children, ...rest } = props;

  return (
    <MuiTableCell
      {...rest}
      sx={{
        // Add spacing between all direct child elements
        '& > *': {
          '&:not(:last-child)': {
            marginBottom: '0.5rem',
          },
        },
        // Convert text spans to block elements for proper line breaks
        '& > span': {
          display: 'block',
          '&:empty': {
            height: '1em', // Preserve empty line spacing
          },
        },
      }}
    >
      {children}
    </MuiTableCell>
  );
};

export const TableRow = MuiTableRow;

export const TableBody = (props: z.input<typeof tableBodySchema>) => {
  return (
    <MuiTableBody
      sx={({ vars }) => ({
        '& tr': {
          // make each other row a different color
          '&:nth-of-type(odd)': {
            backgroundColor: vars.palette.background.default,
          },
          '&:nth-of-type(even)': {
            backgroundColor: vars.palette.background.paperElevation0,
          },
        },
      })}
      {...props}
    />
  );
};

export const Table = (props: z.input<typeof tableSchema>) => {
  const { caption, children } = props;

  return (
    <TableContainer>
      <MuiTable
        sx={{
          minWidth: '100%',
          border: (theme) => `1px solid ${theme.vars.palette.divider}`,
          '& th p, & td p': {
            fontFamily: 'inherit',
            fontWeight: 'inherit',
            fontSize: '.875rem',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
          },
        }}
      >
        {caption ? <caption>{caption}</caption> : null}
        {children}
      </MuiTable>
    </TableContainer>
  );
};

export const TableHeader = (props: z.input<typeof tableHeaderSchema>) => {
  return (
    <MuiTableHeader
      sx={({ vars }) => ({
        '& .MuiTableCell-head': { backgroundColor: vars.palette.primary.lighter },
        '& span': { fontWeight: 600 },
      })}
      {...props}
    />
  );
};

export const TableHeaderCell = (props: z.input<typeof tableHeaderCellSchema>) => {
  const { children } = props;

  return (
    <TableCell>
      <Typography variant="h4">{children}</Typography>
    </TableCell>
  );
};

const documentationURL = getComponentDocumentationUrl('tables');

const cellDescriptions = {
  colspan: 'The number of columns that the cell should span.',
  rowspan: 'The number of rows that the cell should span.',
  align: 'The horizontal alignment of the content in the cell.',
  valign: 'The vertical alignment of the content in the cell.',
  width: 'The width of the cell.',
  height: 'The height of the cell.',
};

const descriptions = {
  tableCell: {
    schema: 'Creates a cell in a table.',
    example: '**Example**\n```mdx\n<TableCell>\n  The content of the cell\n</TableCell>\n```\n',
    children: 'The content of the cell.',
    ...cellDescriptions,
  },
  tableHeaderCell: {
    schema: 'Specifies that the content of this element is within the header of the table.',
    example: '**Example**\n```mdx\n<TableHeaderCell>\n  The content of the cell\n</TableHeaderCell>\n```\n',
    children: 'The content of the cell. Stylized as a header.',
    ...cellDescriptions,
  },
  tableRow: {
    schema: 'Creates a row in a table.',
    example:
      '**Example**\n```mdx\n<TableRow>\n  <TableCell>\n    The content of the cell\n  </TableCell>\n</TableRow>\n```\n',
    children: 'The content of the row consisting of a list of `TableCell`.',
  },
  tableHeader: {
    schema: 'Creates a header in a table.',
    example:
      '**Example**\n```mdx\n<TableHeader>\n  <TableRow>\n    <TableHeaderCell>\n      The content of the cell\n    </TableHeaderCell>\n  </TableRow>\n</TableHeader>\n```\n',
    children: 'The content of the header consisting of a list of a `TableRow`.',
  },
  tableBody: {
    schema: 'Creates a body in a table.',
    example:
      '**Example**\n```mdx\n<TableBody>\n  <TableRow>\n    <TableCell>\n      The content of the cell\n    </TableCell>\n  </TableRow>\n</TableBody>\n```\n',
    children: 'The content of the body consisting of a list of `TableRow`.',
  },
  table: {
    schema: 'Creates a table.',
    example: `**Example**\n\`\`\`mdx
<Table>
  <TableHeader>
    <TableRow>
      <TableHeaderCell>
        The content of the cell
      </TableHeaderCell>
    </TableRow>
  </TableHeader>
  <TableBody>
    <TableRow>
      <TableCell>
        The content of the cell
      </TableCell>
    </TableRow>
  </TableBody>
</Table>\n\`\`\`\n`,
    link: '[Link to the documentation](https://hxdocs.vercel.app/docs/en-us/components/tables)',
    caption: 'The caption of the table.',
    children: 'The content of the table. Usually a table.',
  },
};

const cellSchema = {
  colspan: z.number().describe(descriptions.tableCell.colspan).optional(),
  rowspan: z.number().describe(descriptions.tableCell.rowspan).optional(),
  align: z.enum(['left', 'center', 'right', 'justify', 'char']).describe(descriptions.tableCell.align).optional(),
  valign: z.enum(['top', 'middle', 'bottom', 'baseline']).describe(descriptions.tableCell.valign).optional(),
  width: z.string().describe(descriptions.tableCell.width).optional(),
  height: z.string().describe(descriptions.tableCell.height).optional(),
};

export const tableCellSchema = z
  .object({
    children: z.any().describe(descriptions.tableCell.children).optional(),
    ...cellSchema,
  })
  .describe(`${descriptions.tableCell.schema}\n\n${descriptions.tableCell.example}\n\n${documentationURL}`);

export const tableHeaderCellSchema = z
  .object({
    children: z.any().describe(descriptions.tableHeaderCell.children),
    ...cellSchema,
  })
  .describe(`${descriptions.tableHeaderCell.schema}\n\n${descriptions.tableHeaderCell.example}\n\n${documentationURL}`);

export const tableRowSchema = z
  .object({
    children: z.any().describe(descriptions.tableRow.children),
  })
  .describe(`${descriptions.tableRow.schema}\n\n${descriptions.tableRow.example}\n\n${documentationURL}`);

export const tableHeaderSchema = z
  .object({
    children: z.any().describe(descriptions.tableHeader.children),
  })
  .describe(`${descriptions.tableHeader.schema}\n\n${descriptions.tableHeader.example}\n\n${documentationURL}`);

export const tableBodySchema = z
  .object({
    children: z.any().describe(descriptions.tableBody.children),
  })
  .describe(`${descriptions.tableBody.schema}\n\n${descriptions.tableBody.example}\n\n${documentationURL}`);

export const tableSchema = z
  .object({
    caption: z.string().describe(descriptions.table.caption).optional(),
    children: z.any().describe(descriptions.table.children),
  })
  .describe(`${descriptions.table.schema}\n\n${descriptions.table.example}\n\n${documentationURL}`);
